import * as React from "react";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { MinusCircle, PlusCircle } from "@phosphor-icons/react/dist/ssr";

import { Column, ExpandableRowConfig } from "./types";

export function CustomRow({
	row,
	columns,
	isSelected,
	setIsSelected,
	hideSelect = false,
	expandableRow,
	isExpanded = false,
	onExpand,
	isAtEnd = false,
	isAllDataSelected = false,
	allActionsColumnsAreHidden = false,
	onRowClick,
	isRowClickable,
}: {
	row: any;
	columns: Column[];
	isSelected: boolean;
	setIsSelected: (isSelected: boolean) => void;
	hideSelect?: boolean;
	expandableRow?: ExpandableRowConfig;
	isExpanded?: boolean;
	onExpand?: () => void;
	isAtEnd?: boolean;
	isAllDataSelected?: boolean;
	allActionsColumnsAreHidden?: boolean;
	/** Makes the whole row open the record, so the small action icon stops being the only way in. */
	onRowClick?: (row: any) => void;
	/** Rows this returns false for stay inert — no pointer on something that will not open. */
	isRowClickable?: (row: any) => boolean;
}) {
	const clickable = Boolean(onRowClick) && (isRowClickable ? isRowClickable(row) : true);
	// Anything already interactive inside the row — a checkbox, an action button, a link — keeps
	// its own behaviour; only clicks on the row itself count as "open this".
	const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
		if (!onRowClick || !clickable) return;
		if ((event.target as HTMLElement).closest("button, a, input, label, [role='button']")) return;
		onRowClick(row);
	};

	return (
		<TableRow
			hover={clickable}
			onClick={handleClick}
			selected={isAllDataSelected ? true : isSelected}
			sx={clickable ? { cursor: "pointer" } : undefined}
		>
			{expandableRow?.enabled && (
				<TableCell padding="normal" sx={{ py: 0 }}>
					<IconButton
						size="small"
						color="secondary"
						onClick={onExpand}
						aria-label={isExpanded ? "collapse row" : "expand row"}
					>
						{isExpanded
							? expandableRow.collapseIcon || <MinusCircle size={20} />
							: expandableRow.expandIcon || <PlusCircle size={20} />}
					</IconButton>
				</TableCell>
			)}
			{!hideSelect && (
				<TableCell padding="checkbox">
					<Checkbox
						checked={isAllDataSelected ? true : isSelected}
						onClick={() => setIsSelected(!isSelected)}
						disabled={isAllDataSelected}
						slotProps={{
							input: {
								id: `${row.id}-checkbox`,
								"aria-label": `${row.id} checkbox`,
							},
						}}
					/>
				</TableCell>
			)}
			{columns.map((column) => (
				<TableCell
					key={column.accessorKey}
					sx={{
						visibility: column.accessorKey === "actions" && allActionsColumnsAreHidden ? "hidden" : "visible",
						py: column.accessorKey === "actions" ? "0px" : "auto",
						...(column.accessorKey === "actions" && {
							position: "sticky",
							right: 0,
							zIndex: 1,
							backgroundColor: "background.paper",
							"&:before": {
								transition: "box-shadow 0.5s",
								content: '""',
								position: "absolute",
								top: 0,
								left: 0,
								boxShadow: isAtEnd && !isSelected ? "none" : "0px 0px 4px 0px var(--mui-palette-divider)",
								width: "100%",
								height: "100%",
							},
						}),
					}}
				>
					{column.getCellValue?.(row) === null ? "" : column.getCellValue?.(row) || row?.[column?.accessorKey]}
				</TableCell>
			))}
		</TableRow>
	);
}
